<template>
  <div class="full-width">
    <div class="q-gutter-y-lg market-report q-mx-auto">
      <template v-if="!properties.length">
        <q-card flat bordered>
          <q-skeleton class="google-map" animation="blink" />
        </q-card>
        <div class="row q-col-gutter-sm">
          <div
            v-for="index in 4"
            :key="index"
            class="col-3"
            :class="{ 'col-6': $q.screen.lt.md, 'col-12': $q.screen.lt.md }"
          >
            <q-card flat bordered>
              <q-card-section class="row justify-between items-center">
                <div class="text-h6">
                  <q-skeleton animation="blink" style="width: 100px" />
                </div>
                <div class="trailing-text">
                  <q-skeleton animation="blink" style="width: 125px" />
                </div>
              </q-card-section>
              <q-separator />
              <q-card-section class="row q-col-gutter-x-md q-col-gutter-y-xs">
                <div
                  v-for="ins in 6"
                  :key="ins"
                  class="text-caption col-12 justify-between flex"
                  :class="{ 'col-6': $q.screen.lt.md }"
                >
                  <span class="text-grey-7">
                    <q-skeleton animation="blink" type="text" style="width: 150px" />
                  </span>
                  <span class="q-ml-xs highlighted-number">
                    <q-skeleton animation="blink" type="text" style="width: 50px" />
                  </span>
                </div>
              </q-card-section>
            </q-card>
          </div>
        </div>
        <q-card flat bordered>
          <div id="intercom-header" class="q-pa-md flex justify-between">
            <div>
              <div class="text-h6">
                <q-skeleton style="width: 150px" animation="blink" />
              </div>
              <div style="font-size: 12px">
                <q-skeleton type="text" style="width: 400px" animation="blink" />
              </div>
            </div>
            <div class="flex q-gutter-sm">
              <q-skeleton style="width: 150px" animation="blink" />
            </div>
          </div>
          <q-separator />
          <q-card-section>
            <q-skeleton style="width: 100%; height: 300px" animation="blink" />
          </q-card-section>
        </q-card>
      </template>
      <template v-else-if="report">
        <market-report-google-map
          v-if="isActiveSection('insights')"
          id="intercom-market-report-google-map"
          :filtered-properties="filteredProperties"
          :filters="report.filters"
        />
        <warning-banner v-if="filteredProperties.length === 1000">
          Your market report exceeds the <b>1,000</b> property limit. A random sample of properties
          within your filters is currently displayed. Refine your filters to view all data.
          <template #action>
            <q-btn
              style="width: 114px"
              :to="{ name: 'Edit Regional Report', params: { reportId: report.id } }"
              :color="$q.dark.isActive ? 'deep-orange-1' : 'deep-orange-10'"
              dense
              outline
              no-caps
            >
              Edit
            </q-btn>
          </template>
        </warning-banner>
        <property-compare-unit-type-metrics
          v-if="isActiveSection('insights')"
          id="intercom-property-compare-unit-type-availability"
          :filtered-properties="filteredProperties"
          :date-range="dateRange"
        />
        <market-report-latest-rents
          v-if="isActiveSection('insights')"
          id="intercom-market-report-latest-rents"
          :filtered-properties="filteredProperties"
        />
        <property-compare-bubble-chart
          v-if="isActiveSection('insights')"
          id="intercom-property-compare-chart-compare"
          :filtered-properties="filteredProperties"
          :expanded-unit-history="{}"
          :date-range="dateRange"
          printing-mode
          class="break-after"
        />
        <property-compare-rent-trends
          v-if="isActiveSection('insights')"
          id="intercom-property-compare-chart-history"
          :filtered-properties="filteredProperties"
          :expanded-unit-history="{}"
          :date-range="dateRange"
          :listings-selection-value="
            listingsSelection.length === 1 ? listingsSelection[0].value : 'all'
          "
          printing-mode
        />
        <property-compare-volume-over-time
          v-if="isActiveSection('insights')"
          id="intercom-property-compare-volume-over-time"
          :filtered-properties="filteredProperties"
          :expanded-unit-history="{}"
          :date-range="dateRange"
          :listings-selection-value="
            listingsSelection.length === 1 ? listingsSelection[0].value : 'all'
          "
          :unit-types="selectedUnitType.map((_) => _.value)"
          :included-amenities="includedAmenities.map((_) => _.value)"
          :excluded-amenities="excludedAmenities.map((_) => _.value)"
          :lease-terms="leaseTermSelectionSet"
          :sqft-range="sqftRange"
          printing-mode
          regional-report-mode
        />
        <property-compare-future-availability
          v-if="isActiveSection('insights')"
          id="intercom-property-compare-future-availability"
          :filtered-properties="filteredProperties"
          :expanded-unit-history="{}"
          :date-range="dateRange"
          printing-mode
        />
        <div class="q-mt-xl text-center text-grey-8">
          Help us make HelloData even better! Share your feedback, feature requests, and data
          visualization ideas by leaving us a message
          <span class="text-primary cursor-pointer" @click="openIntercom">in our chat box.</span>
        </div>
      </template>
    </div>
  </div>
  <safe-teleport to="#toolbar-portal">
    <q-toolbar
      id="intercom-toolbar-1"
      class="q-px-sm row justify-center"
      :class="{ 'q-px-md': $q.screen.gt.xs, 'q-px-lg': $q.screen.gt.md }"
      :style="`border-bottom: 1px solid ${$q.dark.isActive ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.12)'}`"
    >
      <div class="market-report full-width row">
        <div>
          <div style="font-size: 20px; font-weight: 500">
            <div class="row q-gutter-x-sm q-pt-sm">
              <div>{{ pageName }}</div>
              <div id="intercom-last-updated">
                <q-btn
                  no-caps
                  color="primary"
                  flat
                  clickable
                  dense
                  size="sm"
                  class="q-px-sm"
                  @click="$tracker.openedPopup('market_filters')"
                >
                  Active Filters ({{ reportFilters.length }})
                  <q-icon name="arrow_drop_down" size="small" />
                  <q-menu transition-duration="100">
                    <q-separator />
                    <q-markup-table flat dense square>
                      <thead>
                        <tr class="text-left">
                          <th class="text-left" scope="col">Filter</th>
                          <th class="text-left" scope="col">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="x in reportFilters" :key="x.label">
                          <td>
                            {{ x.label }}
                          </td>
                          <td>{{ x.value }}</td>
                        </tr>
                      </tbody>
                    </q-markup-table>
                  </q-menu>
                </q-btn>
              </div>
            </div>
          </div>
          <q-tabs
            id="intercom-tabs"
            v-model="activeTab"
            :active-color="report === null ? 'accent' : 'primary'"
            dense
            no-caps
            align="left"
            @update:model-value="(tab) => $tracker.changedTab(activeTab, tab, 'market_report')"
          >
            <template v-for="tab in tabs" :key="tab.name">
              <q-tab
                v-if="tab.displayed"
                :id="`intercom-tabs-${tab.name}`"
                :disable="tab.disable"
                class="tab q-px-none q-mr-md"
                :ripple="false"
                :name="tab.name"
              >
                {{ tab.label }}
              </q-tab>
            </template>
          </q-tabs>
        </div>
        <q-space v-if="$q.screen.gt.xs" />
        <div class="q-gutter-sm self-center">
          <slot name="primay-button">
            <q-btn
              v-if="report && reportInOrg"
              no-caps
              unelevated
              color="primary"
              :to="{ name: 'Edit Regional Report', params: { reportId: report.id } }"
            >
              <q-icon name="edit" size="medium" />
              <span v-if="$q.screen.gt.sm" class="q-ml-sm">Edit Report</span>
            </q-btn>
          </slot>
        </div>
      </div>
    </q-toolbar>
    <q-toolbar
      id="intercom-toolbar-2"
      class="row q-px-sm justify-center"
      :class="{ 'q-px-md': $q.screen.gt.xs, 'q-px-lg': $q.screen.gt.md }"
      :style="`border-bottom: 1px solid ${$q.dark.isActive ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.12)'}`"
    >
      <div class="market-report full-width row justify-end q-gutter-sm">
        <div v-if="$q.screen.gt.sm" class="text-grey-7 text-bold self-center">Report Filters</div>
        <select-options
          id="intercom-toolbar-2-unit-type-filter"
          v-model="selectedUnitType"
          :options-function="getUnitTypeOptions"
          label="Unit Types"
          grouped-selections-key="bed"
          grouped-selections-label="br"
          no-empty
          @update:model-value="(ut) => $tracker.changedFilter('unit_types', 'market_filters', ut)"
        />
        <min-max-filter
          :min="sqftRange.min"
          :max="sqftRange.max"
          use-values-for-label
          title="Square Feet"
          unit="ft²"
          @update-range="changedSqftRange"
          @hide="changedSqftRange"
        >
          Unit SF
        </min-max-filter>
        <select-options
          id="intercom-toolbar-2-listings-filter"
          v-model="listingsSelection"
          :options="listingsOptions"
          label="Listing Status"
          no-empty
          @update:model-value="
            (value) => $tracker.changedFilter('market_availability', 'market_filters', value)
          "
        />
        <include-exclude-select-options
          title="Unit Amenities"
          label="Amenities"
          :options-function="getAmenitiesList"
          tooltip="Extracted from unit-level text available on property websites and floorplan images."
          :aliases="{ 'affordable.lihtc': 'Affordable LIHTC' }"
          @apply="handleAmenityApply"
        />
        <date-range-picker
          id="intercom-toolbar-2-date-filter"
          title="Date Availability"
          :date-range="dateRange"
          @update="changedDateRange"
        />
      </div>
    </q-toolbar>
  </safe-teleport>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { addDays, format } from 'date-fns'
import { useUserStore } from '@/stores/user'
import { useRentsourceStore } from '@/stores/rentsource'
import { getLatestPricesByFilter, getUnitTypes } from 'common/utils'
import type {
  MarketReportSubscriptionAttributes,
  SimpleAvailability,
  SimplePropertyDetailsResponse,
  UnitType
} from 'common'
import { getUnitsByFilter } from 'common'
import DateRangePicker from '@/components/DateRangePicker.vue'
import MarketReportGoogleMap from './MarketReportGoogleMap.vue'
import { unitTypeColor } from '@/utils'
import SelectOptions, { type Option } from '@/components/SelectOptions.vue'
import PropertyCompareUnitTypeMetrics from '../PropertyReports/PropertyCompareUnitTypeMetrics.vue'
import MarketReportLatestRents from './MarketReportLatestRents.vue'
import PropertyCompareBubbleChart from '../PropertyReports/PropertyCompareBubbleChart.vue'
import PropertyCompareRentTrends from '../PropertyReports/PropertyCompareRentTrends.vue'
import PropertyCompareVolumeOverTime from '../PropertyReports/PropertyCompareVolumeOverTime.vue'
import PropertyCompareFutureAvailability from '../PropertyReports/PropertyCompareFutureAvailability.vue'
import IncludeExcludeSelectOptions from '@/components/IncludeExcludeSelectOptions.vue'
import MinMaxFilter from '@/components/PropertyReports/MinMaxFilter.vue'
import WarningBanner from '@/components/WarningBanner.vue'

declare const Intercom: (action: string, ...args: any[]) => void // eslint-disable-line

type Tab = 'insights'

export default {
  name: 'MarketReport',
  components: {
    WarningBanner,
    MinMaxFilter,
    IncludeExcludeSelectOptions,
    MarketReportGoogleMap,
    SelectOptions,
    DateRangePicker,
    PropertyCompareUnitTypeMetrics,
    MarketReportLatestRents,
    PropertyCompareBubbleChart,
    PropertyCompareRentTrends,
    PropertyCompareVolumeOverTime,
    PropertyCompareFutureAvailability
  },
  props: {
    report: {
      type: Object as PropType<MarketReportSubscriptionAttributes | null>,
      default: null
    }
  },
  data() {
    return {
      unitAmenitiesTicked: [] as string[],
      amenityTagsSelection: [] as string[],
      loading: false,
      properties: [] as SimplePropertyDetailsResponse[],
      activeTab: 'insights' as Tab,
      selectedUnitType: [] as {
        label: string
        value: { bed: number; bath: number; partial_bath: number | null }
      }[],
      listingsSelection: [
        { label: 'Active', value: 'active' },
        { label: 'Closed', value: 'closed' }
      ] as {
        label: string
        value: 'all' | 'active' | 'closed'
      }[],
      dateRange: { from: addDays(new Date(), -30), to: new Date() } as {
        from: Date
        to: Date
      },
      listingsOptions: [
        { label: 'Active', value: 'active' },
        { label: 'Closed', value: 'closed' }
      ] as { label: string; value: 'all' | 'active' | 'closed' }[],
      includedAmenities: [] as Option[],
      excludedAmenities: [] as Option[],
      leaseTermSelectionSet: new Set<number | null>(),
      sqftRange: { min: null, max: null } as { min: number | null; max: number | null }
    }
  },
  computed: {
    reportFilters() {
      if (this.report) {
        const formatRangeFilter = (val: { min: number | null; max: number | null }) => {
          if (val.min && val.max) {
            return `Between ${val.min} - ${val.max}`
          } else if (val.min) {
            return `Greater than ${val.min}`
          } else if (val.max) {
            return `Less than ${val.max}`
          }
          return null
        }
        return [
          { label: 'Year Built', filter: this.report.filters.year_built },
          { label: '# Units', filter: this.report.filters.number_units },
          { label: '# Stories', filter: this.report.filters.number_stories }
        ]
          .map(({ label, filter }) => ({ label, value: formatRangeFilter(filter) }))
          .filter((_) => _.value)
      }
      return []
    },
    latestPricesByDateRangeOnly() {
      // use to count the number of units for each unit type and building in the toolbar only
      return this.properties.map((prop) => {
        return {
          prop,
          units: getLatestPricesByFilter(prop, this.dateRange.from, this.dateRange.to)
        }
      })
    },
    filteredProperties() {
      const includedAmenities = this.includedAmenities.length
        ? this.includedAmenities.map((_) => _.value)
        : undefined
      const excludedAmenities = this.excludedAmenities.length
        ? this.excludedAmenities.map((_) => _.value)
        : undefined
      const unitTypes = this.selectedUnitType.map((_) => _.value)

      return this.properties.flatMap((prop) => {
        return {
          prop,
          units: getUnitsByFilter(
            prop,
            getLatestPricesByFilter(
              prop,
              this.dateRange.from,
              this.dateRange.to,
              this.sqftRange.min,
              this.sqftRange.max,
              unitTypes,
              includedAmenities,
              excludedAmenities
            ),
            this.dateRange.to,
            this.listingsSelection.length === 1 ? this.listingsSelection[0].value : 'all'
          )
        }
      })
    },
    reportInOrg() {
      return useUserStore().reportInOrg(this.report)
    },
    tabs(): { label: string; name: Tab; disable: boolean; displayed: boolean }[] {
      return [
        {
          label: 'Insights',
          name: 'insights',
          displayed: true,
          disable: false
        }
      ]
    },
    pageName() {
      return this.report?.name ?? 'Regional Report'
    }
  },
  watch: {
    activeTab() {
      const el = document.getElementById('scroll-anchor')
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
      }
    }
  },
  created() {
    if (this.report) {
      useRentsourceStore()
        .marketSearch(this.report.filters)
        .then((data) => {
          this.properties = data.properties
        })
    }
  },
  methods: {
    unitTypeLabel(unitType: { bed: number; bath: number; partial_bath: number | null }) {
      return `${unitType.bed}BR / ${unitType.bath}${unitType.partial_bath ? '.5' : ''}BA`
    },
    getUnitTypeOptions(): Option[] {
      return getUnitTypes(this.properties).map((ut: UnitType) => ({
        label: this.unitTypeLabel(ut),
        value: ut,
        count: this.latestPricesByDateRangeOnly
          .flatMap((_) => _.units.map((_) => _.unit))
          .filter(
            (_: SimpleAvailability) =>
              _.bed === ut.bed && _.bath === ut.bath && _.partial_bath === ut.partial_bath
          ).length,
        color: unitTypeColor(ut.bed)
      }))
    },
    getAmenitiesList() {
      const toTitle = (label: string) => {
        let title = label.split('_').join(' ')
        if (title.includes('.')) {
          title = title.split('.').reverse().join(' ')
        }
        return title
      }
      const tags = {} as { [tag: string]: Option }
      for (const prop of this.properties) {
        for (const unit of prop.building_availability) {
          if (!unit.tags) {
            continue
          }
          for (const tag of unit.tags) {
            // TODO: Better handling of Direction Views, ideally include direction in amenity tag
            if (tag !== 'view.direction') {
              // per Marc, exclude this tag from the filter
              const count = (tags[tag]?.count || 0) + 1
              tags[tag] = { value: tag, label: toTitle(tag), count: count }
            }
          }
        }
      }
      return Object.values(tags)
    },
    isActiveSection(tab: Tab) {
      return this.activeTab === tab
    },
    changedDateRange(range: { from: Date; to: Date }) {
      this.$tracker.changedFilter('market_date_range', 'market_filters', {
        from: format(range.from, 'yyyy-MM-dd'),
        to: format(range.to, 'yyyy-MM-dd')
      })
      this.dateRange = range
    },
    openIntercom() {
      this.$tracker.openedIntercom()
      Intercom('show')
    },
    handleAmenityApply(event: { include: Option[]; exclude: Option[] }) {
      this.includedAmenities = [...event.include]
      this.excludedAmenities = [...event.exclude]
      this.$tracker.changedFilter('amenities', 'market_filters', {
        includedTags: this.includedAmenities,
        excludedTags: this.excludedAmenities
      })
    },
    changedSqftRange(range: { min: number | null; max: number | null }) {
      this.sqftRange = {
        min: range.min,
        max: range.max
      }
      this.$tracker.changedFilter('sqft_range', 'market_filters', {
        min: range.min,
        max: range.max
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.market-report {
  max-width: 1600px;
}
.tab {
  font-size: 12px;
}
.google-map {
  width: 100%;
  min-height: 300px;
  height: calc(100vh / 4);
}
</style>
